import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppStore } from "../store/useAppStore";
import { DesktopHeaderBar } from "../components/layout/AppShell";
import { Chip } from "../components/ui/Chip";
import { StateTrack } from "../components/ui/StateTrack";
import type { SituationCategory } from "../domain/types";

type Filter = "all" | "work-order" | "move-in" | "move-out" | "support" | "inspections";

const filters: Array<{ key: Filter; label: string }> = [
  { key: "all", label: "All" },
  { key: "work-order", label: "Vendors" },
  { key: "inspections", label: "Inspections" },
  { key: "move-in", label: "Move-ins" },
  { key: "move-out", label: "Move-outs" },
  { key: "support", label: "Support" }
];

const categoryToFilter: Partial<Record<SituationCategory, Filter>> = {
  "work-order": "work-order",
  "inspection-followup": "work-order",
  "move-in": "move-in",
  "move-out": "move-out",
  support: "support",
  financial: "all",
  tour: "all",
  compliance: "all"
};

export function Work() {
  const activeSiteId = useAppStore((s) => s.demo.activeSiteId);
  const situations = useAppStore((s) => s.situations.filter((sit) => sit.siteId === activeSiteId && sit.status === "open"));
  const inspections = useAppStore((s) => s.inspections.filter((i) => i.siteId === activeSiteId));
  const units = useAppStore((s) => s.units);
  const [filter, setFilter] = useState<Filter>("all");

  const rows = useMemo(
    () => (filter === "all" ? situations : situations.filter((s) => categoryToFilter[s.category] === filter)),
    [situations, filter]
  );

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <DesktopHeaderBar />
      <div>
        <h1 className="font-display text-[24px] font-black tracking-tight text-brand-navy">Work</h1>
        <p className="text-[13px] text-muted-foreground">{situations.length} open items at this site</p>
      </div>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter work">
        {filters.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`touch-target rounded-full border px-3 py-1.5 font-action text-[12px] font-bold ${
              filter === f.key ? "border-brand-navy bg-brand-navy text-white" : "border-border bg-card text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filter === "inspections" ? (
        <ul className="flex flex-col gap-2.5">
          {inspections.map((insp) => {
            const doneCount = insp.items.filter((i) => i.result !== null).length;
            return (
              <li key={insp.id} className="rounded-md border border-border bg-card p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-action text-[14.5px] font-bold text-foreground">{insp.title}</p>
                  <Chip tone={insp.status === "review" ? "attention" : insp.status === "completed" ? "success" : "operational"}>{insp.status.replace("-", " ")}</Chip>
                </div>
                <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                  {doneCount}/{insp.items.length} steps recorded
                </p>
                <Link
                  to={`/inspection/${insp.id}`}
                  className="mt-2.5 inline-flex touch-target items-center justify-center rounded-sm bg-primary px-4 font-action text-[14px] font-bold text-primary-foreground"
                >
                  {insp.status === "not-started" ? "Start inspection" : insp.status === "review" ? "Review actions" : "Resume inspection"}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {rows.map((s) => {
            const unit = units.find((u) => u.id === s.unitId);
            return (
              <li key={s.id}>
                <Link to={`/situations/${s.id}`} className="flex flex-col gap-2 rounded-md border border-border bg-card p-3.5 shadow-elevation-1 hover:border-primary">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-action text-[14.5px] font-bold leading-5 text-foreground">{s.title}</p>
                      {unit && <p className="font-mono text-[10.5px] uppercase text-muted-foreground">{unit.buildingLabel} · {unit.unitNumber}</p>}
                    </div>
                    <Chip tone={s.needsAttention ? "attention" : "operational"}>{s.category.replace("-", " ")}</Chip>
                  </div>
                  {s.workflow && <StateTrack steps={s.workflow.steps} compact />}
                </Link>
              </li>
            );
          })}
          {rows.length === 0 && <p className="rounded-md border border-dashed border-border p-6 text-center text-[13.5px] text-muted-foreground">Nothing here right now.</p>}
        </ul>
      )}
    </div>
  );
}
